import React, { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { truncateEmail } from '../utils/stringUtils';
import Wordmark from './shell/Wordmark';
import Avatar from './shell/Avatar';
import ThemeToggle from './shell/ThemeToggle';
import { APP_NAV, MARKETING_NAV, MARKETING_MORE, isActivePath } from './shell/nav';

// The left rail — desktop-only (lg+), sticky, hairline on its right edge.
// Signed in: the six app destinations, then the account row (→ Profile) and
// the theme row pinned to the bottom. Signed out: the marketing pages, a
// "More" fold for the secondary links, and Sign up / Log in.

const railLink = (on) =>
  `flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium transition-colors ${
    on ? 'text-fg bg-raised' : 'text-fg-2 hover:text-fg hover:bg-raised'
  }`;

const Sidebar = memo(({ theme, toggleTheme }) => {
  const { user } = useAuth();
  const { pathname } = useLocation();

  return (
    <aside className="hidden lg:flex flex-col sticky top-0 h-screen border-r border-line bg-canvas px-3.5 pt-[22px] pb-[18px]">
      <div className="px-2.5 pb-6">
        <Wordmark to={user ? '/home' : '/'} />
      </div>

      {user ? (
        <>
          <nav className="flex flex-col gap-0.5" aria-label="Primary">
            {APP_NAV.map((item) => (
              <Link key={item.to} to={item.to} className={railLink(isActivePath(pathname, item.to))}>
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="mt-auto flex flex-col gap-1.5 border-t border-line pt-3.5">
            <Link
              to="/me"
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors ${
                isActivePath(pathname, '/me') ? 'text-fg bg-raised' : 'text-fg-2 hover:text-fg hover:bg-raised'
              }`}
              aria-label="Profile"
            >
              <Avatar user={user} />
              <span className="truncate">{truncateEmail(user.email)}</span>
            </Link>
            <div className="flex items-center justify-between px-2.5 pt-1 text-sm text-fg-3">
              <span>Theme</span>
              <ThemeToggle theme={theme} onToggle={toggleTheme} className="!w-8 !h-8" />
            </div>
          </div>
        </>
      ) : (
        <>
          <nav className="flex flex-col gap-0.5" aria-label="Primary">
            {MARKETING_NAV.map((item) => (
              <Link key={item.to} to={item.to} className={railLink(isActivePath(pathname, item.to))}>
                {item.label}
              </Link>
            ))}
            <details className="mt-1.5 group" open={MARKETING_MORE.some((m) => isActivePath(pathname, m.to)) || undefined}>
              <summary className="list-none cursor-pointer flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-fg-3 hover:text-fg hover:bg-raised [&::-webkit-details-marker]:hidden">
                More
                <span className="mr-1 w-[7px] h-[7px] border-r-[1.5px] border-b-[1.5px] border-current rotate-45 group-open:-rotate-[135deg] transition-transform" aria-hidden="true" />
              </summary>
              {MARKETING_MORE.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`block pl-[18px] pr-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                    isActivePath(pathname, item.to) ? 'text-fg bg-raised' : 'text-fg-3 hover:text-fg hover:bg-raised'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </details>
          </nav>

          <div className="mt-auto flex flex-col gap-2 border-t border-line pt-3.5">
            <Link to="/register" className="pill-cta w-full">Sign up</Link>
            <Link to="/login" className="pill-ghost w-full">Log in</Link>
            <div className="flex items-center justify-between px-2.5 pt-1 text-sm text-fg-3">
              <span>Theme</span>
              <ThemeToggle theme={theme} onToggle={toggleTheme} className="!w-8 !h-8" />
            </div>
          </div>
        </>
      )}
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
